import { z } from 'zod';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';
import type {
    ForensicOptionSearchRequest,
    ForensicOrganizationReference,
    ForensicReferenceOption,
    ForensicRoleAssignmentReference,
    ForensicSessionOption,
    ForensicUserReference,
    ListForensicReferenceOptionsPageResponse,
    ListForensicSessionOptionsPageResponse,
    ListRecentRoleActivityPageResponse,
    ListRecentUserActivityPageResponse,
    ListTransactionLogPageResponse,
    RecentActivityOrganizationReference,
    RecentActivityRoleAssignmentReference,
    RecentActivityUserReference,
    RecentRoleActivityListItem,
    RecentRoleActivityOperation,
    RecentRoleActivitySearchRequest,
    RecentUserActivityListItem,
    RecentUserActivityOperation,
    RecentUserActivitySearchRequest,
    TransactionLogTableRowSnapshot,
    TransactionLogDeleteListItem,
    TransactionLogImpersonationEndedListItem,
    TransactionLogImpersonationStartedListItem,
    TransactionLogInsertListItem,
    TransactionLogListItem,
    TransactionLogLoginListItem,
    TransactionLogLogoutSuccessListItem,
    TransactionLogMfaSuccessListItem,
    TransactionLogOperation,
    TransactionLogRoleAssignmentActivatedListItem,
    TransactionLogRowDetails,
    TransactionLogSearchRequest,
    TransactionLogSort,
    TransactionLogSessionExpiredListItem,
    TransactionLogTableName,
    TransactionLogUpdateListItem,
} from '../transaction-log.types';
import {
    TransactionLogSortDirections,
    TransactionLogSortFields,
} from '../transaction-log.types';
import {
    TransactionLogOperations,
    TransactionLogTableNames } from '../transaction-log.types';
import { Role } from '../identity-contracts';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import { JsonObjectSchema, NonEmptyStringSchema } from './common.schemas';
import {
    SearchFieldPredicatesSchema,
    PhysicalJsonStructurePredicatesSchema,
    SearchTermsSchema,
} from './omnisearch.schemas';

export const TransactionLogTableNameSchema: z.ZodType<TransactionLogTableName> =
    z.enum(TransactionLogTableNames);

export const TransactionLogOperationSchema: z.ZodType<TransactionLogOperation> = z.enum([
    ...TransactionLogOperations,
]);

export const TransactionLogTableRowSnapshotSchema: z.ZodType<TransactionLogTableRowSnapshot> =
    JsonObjectSchema;

export const TransactionLogRowDetailsSchema: z.ZodType<TransactionLogRowDetails> =
    JsonObjectSchema;

export function normalizeTransactionLogTableRowSnapshotStorageV1(
    snapshot: TransactionLogTableRowSnapshot
): TransactionLogTableRowSnapshot {
    return snapshot;
}

export const TransactionLogTableRowSnapshotStorageContract =
    defineStoredJsonContract({
        contractName: 'TransactionLogTableRowSnapshot',
        semanticFieldValues: {},
        variants: {
            v1: {
                schemaName: 'TransactionLogTableRowSnapshotSchema',
                schema: TransactionLogTableRowSnapshotSchema,
                guards: [],
                normalize:
                    normalizeTransactionLogTableRowSnapshotStorageV1,
                semanticPaths: {},
                unrepresentedSemanticFields: [],
            },
        },
        writerVariant: 'v1',
    });

export const StoredTransactionLogTableRowSnapshotParser:
    z.ZodType<TransactionLogTableRowSnapshot> =
    buildStoredJsonValueParser(
        TransactionLogTableRowSnapshotStorageContract
    );

export const TransactionLogPaginationCursorQuerySchema: z.ZodType<string | undefined> = z
    .preprocess(
        (value: unknown): unknown => {
            if (value === undefined || value === null) {
                return undefined;
            }
            const stringParseResult = z.string().safeParse(value);
            if (!stringParseResult.success) {
                return value;
            }
            const trimmed = stringParseResult.data.trim();
            return trimmed.length === 0 ? undefined : trimmed;
        },
        z.string().min(1).optional()
    )
    .optional();

export const TransactionLogSortSchema: z.ZodType<TransactionLogSort> = z
    .object({
        field: z.enum(TransactionLogSortFields),
        direction: z.enum(TransactionLogSortDirections),
        json_path: z.array(z.string().trim().min(1)).min(1).optional(),
    })
    .strict()
    .superRefine((sort, context) => {
        if (sort.field === 'row_details' && sort.json_path === undefined) {
            context.addIssue({
                code: 'custom',
                path: ['json_path'],
                message: 'row_details sorting requires json_path',
            });
        }
        if (sort.field !== 'row_details' && sort.json_path !== undefined) {
            context.addIssue({
                code: 'custom',
                path: ['json_path'],
                message: 'json_path is supported only for row_details sorting',
            });
        }
    });

export const TransactionLogSearchRequestSchema: z.ZodType<TransactionLogSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: TransactionLogPaginationCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: PhysicalJsonStructurePredicatesSchema,
        sort: TransactionLogSortSchema.optional(),
    })
    .strict();

export const ForensicOptionSearchRequestSchema: z.ZodType<ForensicOptionSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: TransactionLogPaginationCursorQuerySchema,
        })
        .strict();

export const ForensicReferenceOptionSchema: z.ZodType<ForensicReferenceOption> =
    z
        .object({
            value: NonEmptyStringSchema,
            label: NonEmptyStringSchema,
        })
        .strict();

export const ForensicSessionOptionSchema: z.ZodType<ForensicSessionOption> =
    z
        .object({
            value: NonEmptyStringSchema,
            label: NonEmptyStringSchema,
            first_ts: z.string().datetime(),
            latest_ts: z.string().datetime(),
        })
        .strict();

export const ListForensicReferenceOptionsPageResponseSchema: z.ZodType<
    ListForensicReferenceOptionsPageResponse
> = z
    .object({
        options: z.array(ForensicReferenceOptionSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

export const ListForensicSessionOptionsPageResponseSchema: z.ZodType<
    ListForensicSessionOptionsPageResponse
> = z
    .object({
        options: z.array(ForensicSessionOptionSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

export const RecentActivityUuidSelectionSchema: z.ZodType<string[] | undefined> =
    z.array(z.string().uuid()).min(1).max(100).optional();

export const RecentUserActivityOperationSchema: z.ZodType<RecentUserActivityOperation> =
    z.enum(['MFA_SUCCESS', 'LOGIN', 'LOGOUT_SUCCESS', 'SESSION_EXPIRED',
]);

export const RecentRoleActivityOperationSchema: z.ZodType<RecentRoleActivityOperation> =
    z.enum([
        'ROLE_ASSIGNMENT_ACTIVATED',
        'IMPERSONATION_STARTED',
        'IMPERSONATION_ENDED',
    ]);

export const ForensicRoleSchema: z.ZodType<ForensicRoleAssignmentReference['role']> =
    z.enum(Role);

export const ForensicUserReferenceSchema: z.ZodType<ForensicUserReference> =
    z
        .object({
            user_uuid: z.string().uuid(),
            primary_email: NonEmptyStringSchema,
        })
        .strict();

export const ForensicOrganizationReferenceSchema: z.ZodType<ForensicOrganizationReference> = z
    .object({
        organization_uuid: z.string().uuid(),
        name: NonEmptyStringSchema,
    })
    .strict();

export const ForensicRoleAssignmentReferenceSchema: z.ZodType<ForensicRoleAssignmentReference> = z
    .object({
        role_assignment_uuid: z.string().uuid(),
        role: ForensicRoleSchema,
        user: ForensicUserReferenceSchema,
        organization: ForensicOrganizationReferenceSchema.nullable(),
    })
    .strict();

export const RecentActivityRoleSchema: z.ZodType<
    RecentActivityRoleAssignmentReference['role']
> = ForensicRoleSchema;

export const RecentActivityUserReferenceSchema: z.ZodType<RecentActivityUserReference> =
    ForensicUserReferenceSchema;

export const RecentActivityOrganizationReferenceSchema: z.ZodType<
    RecentActivityOrganizationReference
> = ForensicOrganizationReferenceSchema;

export const RecentActivityRoleAssignmentReferenceSchema: z.ZodType<
    RecentActivityRoleAssignmentReference
> = ForensicRoleAssignmentReferenceSchema;

interface TransactionLogListItemCommonSchemaShape {
    readonly transaction_log_ref: typeof NonEmptyStringSchema;
    readonly ts: z.ZodString;
    readonly actor: typeof ForensicUserReferenceSchema;
    readonly session_ref: z.ZodNullable<typeof NonEmptyStringSchema>;
    readonly effective_role_assignment: z.ZodNullable<
        typeof ForensicRoleAssignmentReferenceSchema
    >;
    readonly ip_address: z.ZodNullable<typeof NonEmptyStringSchema>;
    readonly user_agent: z.ZodNullable<typeof NonEmptyStringSchema>;
}

const TransactionLogListItemCommonSchemaShape: TransactionLogListItemCommonSchemaShape = {
    transaction_log_ref: NonEmptyStringSchema,
    ts: z.string().datetime(),
    actor: ForensicUserReferenceSchema,
    session_ref: NonEmptyStringSchema.nullable(),
    effective_role_assignment: ForensicRoleAssignmentReferenceSchema.nullable(),
    ip_address: NonEmptyStringSchema.nullable(),
    user_agent: NonEmptyStringSchema.nullable(),
};

type TransactionLogDmlListItemSchemaShape = Omit<
    TransactionLogListItemCommonSchemaShape,
    'effective_role_assignment'
> & {
    readonly table_name: typeof TransactionLogTableNameSchema;
    readonly row_details: typeof TransactionLogRowDetailsSchema;
    readonly effective_role_assignment: typeof ForensicRoleAssignmentReferenceSchema;
};

const TransactionLogDmlListItemSchemaShape: TransactionLogDmlListItemSchemaShape = {
    ...TransactionLogListItemCommonSchemaShape,
    table_name: TransactionLogTableNameSchema,
    row_details: TransactionLogRowDetailsSchema,
    effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
};

export const TransactionLogInsertListItemSchema: z.ZodType<TransactionLogInsertListItem> = z
    .object({
        ...TransactionLogDmlListItemSchemaShape,
        operation: z.literal('INSERT'),
    })
    .strict();

export const TransactionLogUpdateListItemSchema: z.ZodType<TransactionLogUpdateListItem> = z
    .object({
        ...TransactionLogDmlListItemSchemaShape,
        operation: z.literal('UPDATE'),
    })
    .strict();

export const TransactionLogDeleteListItemSchema: z.ZodType<TransactionLogDeleteListItem> = z
    .object({
        ...TransactionLogDmlListItemSchemaShape,
        operation: z.literal('DELETE'),
    })
    .strict();

export const TransactionLogLoginListItemSchema: z.ZodType<TransactionLogLoginListItem> = z
    .object({
        ...TransactionLogListItemCommonSchemaShape,
        operation: z.literal('LOGIN'),
    })
    .strict();

export const TransactionLogMfaSuccessListItemSchema: z.ZodType<TransactionLogMfaSuccessListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('MFA_SUCCESS'),
        })
        .strict();

export const TransactionLogRoleAssignmentActivatedListItemSchema: z.ZodType<TransactionLogRoleAssignmentActivatedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('ROLE_ASSIGNMENT_ACTIVATED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogLogoutSuccessListItemSchema: z.ZodType<TransactionLogLogoutSuccessListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('LOGOUT_SUCCESS'),
        })
        .strict();

export const TransactionLogSessionExpiredListItemSchema: z.ZodType<TransactionLogSessionExpiredListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('SESSION_EXPIRED'),
        })
        .strict();

export const TransactionLogImpersonationStartedListItemSchema: z.ZodType<TransactionLogImpersonationStartedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('IMPERSONATION_STARTED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogImpersonationEndedListItemSchema: z.ZodType<TransactionLogImpersonationEndedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('IMPERSONATION_ENDED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogListItemSchema: z.ZodType<TransactionLogListItem> = z.union([
    TransactionLogInsertListItemSchema,
    TransactionLogUpdateListItemSchema,
    TransactionLogDeleteListItemSchema,
    TransactionLogLoginListItemSchema,
    TransactionLogMfaSuccessListItemSchema,
    TransactionLogRoleAssignmentActivatedListItemSchema,
    TransactionLogLogoutSuccessListItemSchema,
    TransactionLogSessionExpiredListItemSchema,
    TransactionLogImpersonationStartedListItemSchema,
    TransactionLogImpersonationEndedListItemSchema,
]);

export const ListTransactionLogPageResponseSchema: z.ZodType<ListTransactionLogPageResponse> = z
    .object({
        transaction_log: z.array(TransactionLogListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

export const RecentUserActivityListItemSchema: z.ZodType<RecentUserActivityListItem> =
    z
        .object({
            activity_ref: NonEmptyStringSchema,
            ts: z.string().datetime(),
            operation: RecentUserActivityOperationSchema,
            session_ref: NonEmptyStringSchema.nullable(),
            user: RecentActivityUserReferenceSchema,
            effective_role_assignment:
                RecentActivityRoleAssignmentReferenceSchema.nullable(),
            ip_address: NonEmptyStringSchema.nullable(),
            user_agent: NonEmptyStringSchema.nullable(),
        })
        .strict();

export const RecentRoleActivityListItemSchema: z.ZodType<RecentRoleActivityListItem> =
    z
        .object({
            activity_ref: NonEmptyStringSchema,
            ts: z.string().datetime(),
            operation: RecentRoleActivityOperationSchema,
            session_ref: NonEmptyStringSchema.nullable(),
            actor: RecentActivityUserReferenceSchema,
            effective_role_assignment:
                RecentActivityRoleAssignmentReferenceSchema,
            ip_address: NonEmptyStringSchema.nullable(),
            user_agent: NonEmptyStringSchema.nullable(),
        })
        .strict();

export const RecentUserActivitySearchRequestSchema: z.ZodType<RecentUserActivitySearchRequest> =
    z
        .object({
            user_uuids: RecentActivityUuidSelectionSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: TransactionLogPaginationCursorQuerySchema,
        })
        .strict();

export const RecentRoleActivitySearchRequestSchema: z.ZodType<RecentRoleActivitySearchRequest> =
    z
        .object({
            user_uuids: RecentActivityUuidSelectionSchema,
            organization_uuids: RecentActivityUuidSelectionSchema,
            roles: z.array(RecentActivityRoleSchema).min(1).max(20).optional(),
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: TransactionLogPaginationCursorQuerySchema,
        })
        .strict();

export const ListRecentUserActivityPageResponseSchema: z.ZodType<
    ListRecentUserActivityPageResponse
> = z
    .object({
        activity: z.array(RecentUserActivityListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

export const ListRecentRoleActivityPageResponseSchema: z.ZodType<
    ListRecentRoleActivityPageResponse
> = z
    .object({
        activity: z.array(RecentRoleActivityListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();
